import styled, { css } from 'styled-components';

import { colors, media, Theme } from '../../../core';
import { GridColumn, SvgContainerStyles } from '../../styles';
import watermarkURL from './svg/Watermark';

interface FooterInputProps {
  hasEnteredText: boolean;
  hasError?: boolean;
}

const baseDividerStyles = css`
  content: '';
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  background-color: ${({ theme }) => theme.footerLineColor || colors.northrend};
  height: 1px;
`;

export const FooterWrapper = styled.footer`
  background: ${({ theme }) =>
    `radial-gradient(67.36% 217.71% at 22.5% 38.7%, ${
      theme.footerBackgroundLight || '#242e47'
    } 0%, ${theme.footerBackgroundDark || '#12192a'} 100%)`};
  padding: 3.25rem 0 2rem 0;
  position: relative;
  ${({ theme }) =>
    theme.name !== Theme.theme3 &&
    `
      &::before {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        height: 100%;
        width: 100%;
        background-image: url(${watermarkURL});
        background-position: -5.5% 32%;
        background-repeat: no-repeat;
        background-size: 42.5%;
        opacity: 0.2;
        pointer-events: none;
        ${media.tabletLandscape} {
          background-size: 50%;
        }
        ${media.tabletMedium} {
          background-position: 185% 90%;
          background-size: 75%;
        }
        ${media.tabletPortrait} {
          background-position: 250% 90%;
          background-size: 88%;
        }
        ${media.mobileXLarge} {
          background-position: center;
          background-size: cover;
        }
      }
    `}
  ${media.tabletMedium} {
    font-size: 1.125rem;
    padding: 2.75rem 0 1.5rem 0;
    ${GridColumn} {
      &:nth-child(2) {
        margin: 2.5rem 0 1.25rem 0;
      }
    }
  }
`;

export const FooterHeading = styled.span`
  color: ${({ theme }) => theme.footerHeadingColor || colors.edgewater};
  display: inline-block;
  font-size: 0.875rem;
  font-weight: 600;
  letter-spacing: 0.5px;
  line-height: 1.7;
  margin-bottom: 1.5rem;
  text-transform: uppercase;
  ${media.tabletMedium} {
    margin-bottom: 1rem;
  }
`;

export const FooterInputGroup = styled.div`
  margin-bottom: 2rem;
  position: relative;
  width: 19.125rem;
  ${media.tabletMedium} {
    margin-bottom: 3rem;
  }
  svg {
    position: absolute;
    top: 50%;
    right: 1rem;
    transform: translateY(-50%);
    width: 1.25rem;
  }
`;

export const FooterInputLabel = styled.label`
  color: ${({ theme }) => theme.fontColor || colors.darkCerulean};
  line-height: 2;
  text-transform: uppercase;
`;

export const FooterLabelText = styled.span`
  position: absolute;
  top: 50%;
  left: 1rem;
  transform: translateY(-50%);
  font-size: 0.75rem;
  transition: all 300ms ease-in-out;
`;

export const FooterInput = styled.input<FooterInputProps>`
  border-radius: 0.25rem;
  border: none;
  font-size: 1rem;
  padding: 1rem;
  width: 100%;
  transition: padding 300ms ease-in-out;

  &:active,
  &:focus {
    padding: 1.5rem 1rem 1rem 1rem;

    & ~ ${FooterLabelText} {
      font-size: 0.5rem;
      top: 22.5%;
    }
  }

  ${({ hasEnteredText }) =>
    hasEnteredText
      ? css`
          padding: 1.5rem 1rem 1rem 1rem;

          & ~ ${FooterLabelText} {
            font-size: 0.5rem;
            top: 22.5%;
          }
        `
      : ''}

  ${({ hasError, theme }) =>
    hasError
      ? css`
          border: 3px solid ${theme.footerInputErrorColor || theme.errorColor};
        `
      : ''}
`;

export const FooterSocialIcons = styled.div`
  ${media.tabletMedium} {
    ${SvgContainerStyles} {
      svg {
        width: 2rem;
      }
    }
  }
`;

export const FooterMenuWrapper = styled.div`
  display: flex;
  align-items: flex-start;
  ul {
    flex: 1 1 50%;
  }
`;

export const FooterMenu = styled.ul`
  font-weight: 300;
  li {
    &:not(:last-child) {
      margin-bottom: 1.25rem;
    }
    span {
      color: ${colors.white};
    }
    a {
      color: ${({ theme }) => theme.footerTextColor || colors.northrend};
      transition: color 150ms ease-in-out;
      svg {
        path {
          transition: fill 150ms ease-in-out;
        }
      }
      @media (hover) {
        &:hover,
        &:active,
        &:focus {
          color: ${colors.white};
          svg {
            path {
              fill: ${colors.white};
            }
          }
        }
      }
    }
  }
`;

export const FooterBottom = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  grid-column: 1 / -1;
  font-weight: 300;
  margin-top: 3rem;
  padding-top: 2rem;
  position: relative;
  ${media.tabletMedium} {
    align-items: flex-start;
    flex-direction: column-reverse;
    padding-top: 0;
  }
  &::before {
    ${baseDividerStyles}
    top: 0;
    width: calc(100vw - 5.5rem);
    ${media.tabletMedium} {
      display: none;
    }
  }
`;

export const FooterFinancingRow = styled.div`
  display: flex;
  align-items: baseline;
  position: relative;
  ${media.tabletMedium} {
    flex-wrap: wrap;
    margin-bottom: 1.5rem;
    padding-bottom: 1.5rem;
    width: 100%;
    &::before {
      ${baseDividerStyles}
      top: 100%;
      width: 100%;
    }
  }
  div {
    ${media.tabletMedium} {
      margin-top: 0.75rem;
    }
    &:first-of-type {
      margin: 0 1.5rem;
      transform: translateY(4.25px); // needed to properly align paybright logo
      ${media.tabletMedium} {
        margin-left: 0;
      }
    }
  }
`;

export const FooterSpan = styled.span`
  color: ${colors.white};
  font-size: 0.875rem;
  line-height: 1.7;
  ${media.tabletMedium} {
    flex: 1 1 100%;
    font-size: 1rem;
    width: 100%;
  }
`;

export const Link = styled.a``;

export const ScreenReaderText = styled.span`
  &:not(:focus):not(:active) {
    clip: rect(0 0 0 0);
    clip-path: inset(50%);
    height: 1px;
    overflow: hidden;
    position: absolute;
    white-space: nowrap;
    width: 1px;
  }
`;

export const SocialIcons = styled.div`
  display: flex;
  align-items: center;
  div {
    &:first-child {
      margin-right: 0.625rem;
      ${media.tabletMedium} {
        margin-right: 1.25rem;
      }
    }
  }
`;

export const SubmitSpan = styled.span<{ isSubmitFinished?: boolean }>`
  ${({ isSubmitFinished }) =>
    isSubmitFinished
      ? `
      cursor: default; 
      pointer-events: none; 
      opacity: 0.3;
      `
      : `
      cursor: pointer;
      `}
`;
